import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { SUGGESTION_ANNOTATION_COMMAND_TYPES } from '../annotationCommands';
import { manualTableSelectionButton } from '../consts';
import { suggestionAnnotations } from '../data/actions';

type UseManualTableSelectionProps = {
  instance: WebViewerInstance | null;
  isPartLibraryViewer: boolean;
  isBeamsEnabled: boolean;
};

export const useManualTableSelection = ({
  instance,
  isPartLibraryViewer,
  isBeamsEnabled,
}: UseManualTableSelectionProps) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!instance) return;

    const viewer = instance.Core.documentViewer;
    const annotationManager = viewer.getAnnotationManager();

    // Create the rectangle tool
    const rectangleTool = new instance.Core.Tools.RectangleCreateTool(viewer);
    rectangleTool.setStyles({
      StrokeColor: new instance.Core.Annotations.Color(140, 65, 237, 0.05),
      FillColor: new instance.Core.Annotations.Color(140, 65, 237, 0.15),
    });

    // Register the tool if it hasn't been registered yet
    instance.UI.registerTool({
      toolName: 'ManualTableSelection',
      toolObject: rectangleTool,
      buttonImage: 'icon-circle',
      tooltip: 'Add missing table extraction',
    });

    // Get custom tool's element to manually toggle active and inactive button state
    const manualTableSelectionButtonElement = document.getElementsByClassName(
      'manualTableSelectionButton',
    );

    // This will listen for any tool mode changes and update the custom button's active state accordingly
    // PDFTron does not do this for free for custom buttons
    viewer.addEventListener('toolModeUpdated', (newTool) => {
      if (
        manualTableSelectionButtonElement &&
        manualTableSelectionButtonElement[0]
      ) {
        if (newTool.name === 'ManualTableSelection') {
          manualTableSelectionButtonElement[0].classList.add('active');
        } else {
          manualTableSelectionButtonElement[0].classList.remove('active');
        }
      }
    });

    const handleSelection = (
      annotations: Core.Annotations.RectangleAnnotation[] | unknown[],
      action: string,
    ) => {
      // Prevents the handler from being called again on annotation deletion
      if (action !== 'add') {
        return;
      }

      const annot = annotations[0];
      if (
        !(annot instanceof instance.Core.Annotations.RectangleAnnotation) ||
        annot.ToolName !== 'ManualTableSelection' // Ensures that the annotation is from the custom tool
      ) {
        return;
      }

      const pageNumber = annot.PageNumber;
      const rect = annot.getRect();

      annotationManager.setAnnotationStyles(annot, {
        FillColor: new instance.Core.Annotations.Color(0, 0, 0, 0),
        StrokeColor: new instance.Core.Annotations.Color(140, 65, 237, 1),
        StrokeThickness: 2,
        BorderRadius: 24,
        Style: 'dash',
        Dashes: '5, 5',
      });

      dispatch(
        suggestionAnnotations.queueCommands({
          commands: [
            {
              type: SUGGESTION_ANNOTATION_COMMAND_TYPES.REFRESH_EXTRACTED_TABLES,
              payload: {
                pageNumber,
                x1: rect.x1,
                y1: rect.y1,
                x2: rect.x2,
                y2: rect.y2,
                cleanUpTempAnnotation: () => {
                  annotationManager.deleteAnnotation(annot);
                },
              },
            },
          ],
        }),
      );

      instance.UI.setToolMode('Pan');
    };

    // Listen for annotation creation
    annotationManager.addEventListener('annotationChanged', handleSelection);

    // Create the Modular UI toolButton for the toolbar
    if (isBeamsEnabled && !isPartLibraryViewer) {
      // Get the default header
      const defaultHeader = instance.UI.getModularHeader('default-top-header');

      // Create the button configuration
      const buttonConfig = new instance.UI.Components.ToolButton({
        dataElement: 'manualTableSelectionButton',
        toolName: 'ManualTableSelection',
        img: manualTableSelectionButton,
        className: 'manualTableSelectionButton',
      });

      // Add the button directly to the header's items
      const currentItems = defaultHeader.getItems();
      defaultHeader.setItems([...currentItems, buttonConfig]);
    }

    return () => {
      annotationManager.removeEventListener(
        'annotationChanged',
        handleSelection,
      );
    };
  }, [instance, dispatch, isBeamsEnabled, isPartLibraryViewer]);
};
